import { SyntheticEvent, RefObject, MutableRefObject } from 'react'
import { Image } from '@cook-types/cook'
import { ViewportOrientationValues } from '@contexts/viewport'
import * as DarkCovePlayerTypes from '@components/Video/Brightcove/types'
import { Vtype } from '@components/AppNexus/types'
import { RecommendedItem } from '@hooks/useRecommendations'

export type AutoplayType = boolean | 'muted'

export type PlayerComponent = DarkCovePlayerTypes.PlayerComponent

export interface BrightcoveVideoWebPlayer {
  play?: () => void
  pause?: () => void
  mute?: () => void
  seekTo?: (seconds: number) => void
  seekToLive?: () => void
  player?: () => PlayerComponent | undefined
  dispose?: () => void
  getPlayerStatus?: () => void
  closeOverlay?: () => void
}

export enum PlayerType {
  VOD = 'vod',
  LIVE = 'live',
  LIVE_IN_ARTICLE = 'live_in_article',
}

export enum PlayerMode {
  OVERLAY = 'overlay',
  INLINE = 'inline',
}

export enum VideoPlayingStatus {
  PLAYING = 'playing',
  PAUSED = 'paused',
  ENDED = 'ended',
}
export interface UsePlatformSpecificPropsInput {
  allowFloating: boolean
  allowVideoCropping: boolean
  autoplay: boolean
  isAdDisabled: boolean
  isBlickTV: boolean
  isInViewport: boolean | null
  position?: string
  isInArticle?: boolean
  isInVideoOnlyArticle: boolean
  videoTaxonomy?: IABCategory[]
  image?: Omit<Image, 'width' | 'height'>
  duration: number
  switchHeadline?: (headline: string) => void
  syndication: string
  articleId?: string
  videoId: string
  title: string
  hasPlaylist?: boolean
  daiAssetKeyBrightcove?: string
  playerRef?: MutableRefObject<BrightcoveVideoWebPlayer>
  isMainElement?: boolean
}

export interface DarkCoveProps {
  isInViewport: boolean | null
  allowFloating: boolean
  isBlickTV: boolean
  articleId?: string
  videoId?: string
  position?: string
  allowVideoCropping: boolean
  videoTaxonomy?: IABCategory[]
  duration: number
  isAdDisabled: boolean
  syndication: string
  image?: Omit<Image, 'width' | 'height'>
  switchHeadline?: (headline: string) => void
  autoplay: boolean
  title: string
  isInVideoOnlyArticle: boolean
  isInArticle?: boolean
  hasPlaylist?: boolean
}

export type DarkCovePlayerWebProps = UsePlatformSpecificPropsWebOutput & {
  isInViewport: boolean | null
}

export interface DarkCovePlayerWebPropsWithCarriedOverRef
  extends Omit<DarkCovePlayerWebProps, 'ref'> {
  playerRef: UsePlatformSpecificPropsWebOutput['ref']
}

export interface UsePlatformSpecificPropsWebOutput
  extends Omit<
      UseCommonPropsOutputProps,
      | 'trackingCallbacks'
      | 'adMidrollThresholdInSeconds'
      | 'adMidrollOffsetInPercent'
    >,
    UseCommonPropsOutputPropsTrackingCallbacks {
  isInViewport: boolean | null
  autoplay: AutoplayType
  ref: MutableRefObject<BrightcoveVideoWebPlayer | null>
  playerId: string
  volume?: number
  startMuted?: boolean
  startWithCaptions?: boolean
  mountPlayer: boolean
  adContainerId: string
  captionFontSize: number
  reducedControls: boolean
  adTagURL: string
  vastUrl: { [key: string]: any }
  videoRecommendations?: RecommendedItem[]
  videoPlayingStatus?: VideoPlayingStatus
  isOverlay?: boolean
  daiAssetKeyBrightcove?: string
  image?: UsePlatformSpecificPropsInput['image']
  isMainElement?: boolean
  size?: 'small' | 'medium'
}

export type UseCommonPropsInputProps = Pick<
  UsePlatformSpecificPropsInput,
  | 'isBlickTV'
  | 'allowFloating'
  | 'autoplay'
  | 'videoId'
  | 'allowVideoCropping'
  | 'duration'
  | 'switchHeadline'
  | 'title'
  | 'image'
  | 'isAdDisabled'
  | 'position'
  | 'isInVideoOnlyArticle'
  | 'isInViewport'
> & {
  playerRef: UsePlatformSpecificPropsWebOutput['ref']
}

export type UseCommonPropsOutputPropsTrackingCallbacks =
  UseTrackingOutputProps & {
    onHeadlineChange: (e: any) => void
    onAdResumed: () => void
  }

export interface UseCommonPropsOutputProps {
  isBlickTV: boolean
  allowFloating: boolean
  showCaptionsInline: boolean
  showCaptionsOverlay: boolean
  showControlsInline: boolean
  showControlsOverlay: boolean
  showGraphicsInline: boolean
  showGraphicsOverlay: boolean
  accountId: string
  videoId: string
  posterURL: string | undefined
  adsEnabled: boolean
  vtype: Vtype
  skipAd: number
  logoURL: string
  adSkipTimeDurationInSeconds: number
  adMidrollThresholdInSeconds: number
  adMidrollOffsetInPercent: number
  inlineFlag: boolean
  naturalOverlayAspectRatio: boolean
  isInVideoOnlyArticle: boolean
  trackingCallbacks: UseCommonPropsOutputPropsTrackingCallbacks
  isInScribble?: boolean
}

export interface UseTrackingOutputProps {
  onImpression: () => void
  onPlay: () => void
  onPlaySuccess: () => void
  onBuffering: () => void
  onAdRequest: () => void
  onAdStarted: () => void
  onAdSkipped: () => void
  onAdEnded: () => void
  onAdErrored: () => void
  onSeek: () => void
  onProgress: (data?: { currentTime: number }) => void
  onQuartileCompletion: (payload: { percent: number }) => void
  onEnd: () => void
  onForward: () => void
  onBackward: () => void
  onError: ({ error }: any) => void
  onMute: (params?: { buttonPressed?: boolean }) => void
  onUnmute: (params?: { buttonPressed?: boolean }) => void
  onCaptionsEnabled: () => void
  onCaptionsDisabled: () => void
  onEnterOverlay: () => void
  onExitOverlay: () => void
  onOrientationChange: (orientation: ViewportOrientationValues) => void
  setNewHeadline: (e: any) => void
  onChapterClick: (data: ChapterTrackingParams) => void
  onChapterSwipe: (data: ChapterTrackingParams) => void
  onChapterButtonClick: (data: ChapterTrackingParams) => void
  onUnmuteCTAButtonClick: () => void
  onUnmuteCTAButtonImpression: () => void
}

export enum Orientation {
  PORTRAIT = 'portrait',
  HORIZONTAL = 'horizontal',
}

export interface IABCategory {
  id: string
  text: string
}

export interface VideoAd {
  placement: string
  slotCode: string
  slotCodeMobile?: string
  metadata?: {
    iabcategories?: IABCategory[]
  }
}

export interface BrightcoveCallbacks {
  onPlay: (e?: SyntheticEvent) => void
  onHeadlineChange: (e: HeadlineEvent) => void
  onPlaySuccess: (e?: SyntheticEvent) => void
  onBuffering: () => void
  onProgress: (payload: { currentTime: number }) => void
  onAdRequest: () => void
  onAdStarted: () => void
  onAdSkipped: () => void
  onAdEnded: () => void
  onAdErrored: () => void
  onError: () => void
  onEnd: () => void
  onQuartileCompletion: (p: number) => void
  onEnterOverlay: (mode?: string) => void
  onExitOverlay: () => void
  onEnterMiniMode: () => void
  onExitMiniMode: () => void
  onMute: () => void
  onUnmute: () => void
  onCaptionsEnabled: () => void
  onCaptionsDisabled: () => void
}

export interface VideoTrackingCallbacks extends BrightcoveCallbacks {
  onImpression: () => void
  onPreviewCollapse: () => void
  onPreviewExpand: () => void
  setPlayerRef: (ref: RefObject<BrightcoveVideoWebPlayer>) => void
  setAdsEnabled: (adsEnabled: boolean) => void
  setNewHeadline?: (headline: string) => void
}

export interface ScribbleInjectedProps {
  inScribble?: boolean
}

// This data is coming from the API
export interface VideoAPIProps {
  kind: string[]
  ad?: VideoAd
  autoplay?: boolean
  allowVideoCropping?: boolean
  catchword?: string
  catchwordArticle?: string
  duration: number
  image?: Image
  isAdDisabled?: boolean
  isMainElement?: boolean
  title?: string
  titleArticle?: string
  uuid?: string
  videoId: string
  isInVideoOnlyArticle: boolean
}

// after transformation making some fields required and adding some additional ones
export type BrightcoveWidgetProps = {
  kind: string[]
  videoId: string
  duration: number
  durationInSeconds: number
  inline: boolean
  isMainElement?: boolean
  placeholderImage: Image | null
  videoAdPlacement: string
  videoAdUrl?: string
  videoSlotCode?: string
  videoSlotCodeMobile?: string
  vtype: string | undefined
  title: string
}

export interface WebVideoProps extends BrightcoveCallbacks {
  adSkipTimeDurationInSeconds: number
  catchword: string
  duration: string
  durationInSeconds: number
  durationFormatted: string
  allowVideoCropping?: boolean
  isAdDisabled: boolean
  posterURL: string
  isMainElement: boolean
  midrollTimeOffset: string
  overlayPlayerId: string
  previewPlayerId: string
  syndication: string
  videoId: string
  videoSlotCode: string
  videoStatus: string
  vtype: string
  title: string
  videoTaxonomy?: IABCategory[]
  trackingCallbacks: VideoTrackingCallbacks
}

export interface VideoPlayerStatus {
  audio?: boolean | string
  captions?: boolean | string
  playerMode: PlayerMode
}

export interface VideoTrackingParams extends VideoPlayerStatus {
  videoId: string
  duration: number
  playerType: string
  quartileState: number | 'end'
  secondsWatched: number
  videoTitle: string
  floating?: boolean
}

export interface PlaylistShowTrackingParams {
  playlistTitle?: string
}
export interface PlaylistClickTrackingParams {
  articleId: string
}
export interface VideoWidgetImpressionTrackingParams {
  videoId: string
  videoTitle: string
  playerType: 'inline' | 'overlay'
  indexPosition: number
  videoState?: 'pause' | 'end'
}
export interface VideoWidgetClickTrackingParams {
  videoId: string
  videoTitle: string
  playerType: 'inline' | 'overlay'
  indexPosition: number
  videoState?: 'pause' | 'end'
}
export interface VideoWidgetActionTrackingParams {
  videoId: string
  videoTitle: string
  playerType: 'inline' | 'overlay'
  widgetAction: 'play' | 'replay' | 'close'
  videoState?: 'pause' | 'end'
}

export interface VideoWidgetImpressionAureusTrackingParams {
  aureusOfferId?: string
  isRecommendation: true
}

export interface VideoWidgetClickAureusTrackingParams {
  articleId: RecommendedItem['articleId']
  href: RecommendedItem['link']['href']
  aureusOfferId: RecommendedItem['aureusOfferId']
}
export interface ChapterTrackingParams {
  uniqueChapterId?: string
  chapterHeadline?: string
  chapterTimecode?: string
  chapterLength?: number
  buttonLabel?: string
}

export interface ValidPlayerStatus {
  audio: boolean
  captions: boolean
  playerMode: PlayerMode
}

export interface BlickTVContextTrackingDispatchProps {
  action: 'popOut' | 'dismiss' | 'resetExperienceId'
}

export type PlayerStatusTracking = ValidPlayerStatus | null

export type VolatileTrackingData = Pick<
  VideoTrackingParams,
  'quartileState' | 'secondsWatched' | 'videoTitle'
>

export interface VideoErrorTrackingParams extends VideoTrackingParams {
  code?: string
  message?: string
}

// Types are coming from proptypes of: https://github.com/BrightcovePS/ringier-blicktv-web/blob/master/src/Main.js
export interface BrightcoveVideoWebWrapperProps extends BrightcoveCallbacks {
  adsEnabled?: boolean
  adTagURL?: string
  autoplay?: AutoplayType
  reducedControls?: boolean
  inlineFlag?: boolean
  showControlsInline?: boolean
  showGraphicsInline?: boolean
  showCaptionsInline?: boolean
  showControlsOverlay?: boolean
  showGraphicsOverlay?: boolean
  showCaptionsOverlay?: boolean
  logoURL?: string
  midrollTimeOffset?: string | null
  naturalOverlayAspectRatio?: boolean
  noPortraitMode?: boolean
  posterURL?: string
  preferredPeakBitRate?: number
  skipAd?: number
  videoId: string
  setRef: RefObject<BrightcoveVideoWebPlayer>
}

export interface HeadlineEvent {
  id?: string
  headline?: string
}

interface TrackingUtilsCommonProps {
  isFloatingBlickTV: boolean
  playerRef: UsePlatformSpecificPropsWebOutput['ref']
  setDispatchTrackingEvents: (args: any) => void
  createDispatchFutureStateFunction: (args: any) => void
}

export type TrackingGetPlayerStatusProps = TrackingUtilsCommonProps
export interface VideoMetadata {
  id: string
  name?: string
  poster?: string
  thumbnail?: string
}

export type VideoMetadataResponse = VideoMetadata[]

export interface AdTagManagerDaiResponse {
  queryString: { [key: string]: any }
  url: string
}
